import { useCallback, useEffect, useState } from "react";
import { API } from "../../../services/api";
import { emptyPatientContact } from "../model/materielConstants";

const CONFIG = { noRedirectOn401: true };

// Contacts patients d'un service : chargement, ajout d'une ligne vide, enregistrement immédiat de chaque
// modification (annulée si le serveur la refuse) et suppression après confirmation.
export function usePatientContacts(serviceKey) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [savingId, setSavingId] = useState(null);
    const [error, setError] = useState(null);
    const endpoint = `/medical-equipment/services/${serviceKey}/patient-contacts`;

    const refresh = useCallback(() => {
        setLoading(true);
        setError(null);
        API.get(endpoint, CONFIG)
            .then(res => setRows(res.data))
            .catch(() => setError("Impossible de charger les contacts patients."))
            .finally(() => setLoading(false));
    }, [endpoint]);

    useEffect(() => { refresh(); }, [refresh]);

    const addRow = async () => {
        setSavingId("new");
        setError(null);
        try {
            const { data } = await API.post(endpoint, emptyPatientContact, CONFIG);
            setRows(prev => [data, ...prev]);
        } catch {
            setError("Impossible d'ajouter un contact patient.");
        } finally {
            setSavingId(null);
        }
    };

    const patchRow = async (id, patch) => {
        const current = rows.find(row => row.id === id);
        if (!current) return;
        const next = { ...current, ...patch };
        setRows(prev => prev.map(row => row.id === id ? next : row));
        setSavingId(id);
        setError(null);
        try {
            const { data } = await API.patch(`${endpoint}/${id}`, next, CONFIG);
            setRows(prev => prev.map(row => row.id === id ? data : row));
        } catch {
            setRows(prev => prev.map(row => row.id === id ? current : row));
            setError("Impossible d'enregistrer le contact patient.");
        } finally {
            setSavingId(null);
        }
    };

    const deleteRow = async (id) => {
        if (!globalThis.confirm("Supprimer ce contact patient ?")) return;
        const previous = rows;
        setRows(prev => prev.filter(row => row.id !== id));
        try {
            await API.delete(`${endpoint}/${id}`, CONFIG);
        } catch {
            setRows(previous);
            setError("Impossible de supprimer le contact patient.");
        }
    };

    return { rows, loading, savingId, error, refresh, addRow, patchRow, deleteRow };
}
